import type { CnAuth } from "@/lib/cardnexus/auth-context";
import type { CardRecord } from "@/lib/types";
import { searchProducts } from "@/lib/cardnexus/client";

export const CN_PRODUCT_MAP_STORAGE = "rifthub.cardnexus.productMap.v1";
export const CN_CUSTOM_ID_PREFIX = "rifthub:";

export function customIdForCard(cardId: string): string {
  return `${CN_CUSTOM_ID_PREFIX}${cardId}`;
}

export function cardIdFromCustomId(customId: string | null | undefined): string | null {
  if (!customId || !customId.startsWith(CN_CUSTOM_ID_PREFIX)) return null;
  const id = customId.slice(CN_CUSTOM_ID_PREFIX.length).trim();
  return id || null;
}

export function printQueryForCard(card: Pick<CardRecord, "setId" | "collectorNumber" | "name">): string {
  if (card.setId && card.collectorNumber != null) {
    return `${card.setId}-${card.collectorNumber}`;
  }
  return card.name;
}

function scoreProductHit(
  hit: { id: number; name?: string; printNumber?: string | null; expansion?: { code?: string } | null },
  card: Pick<CardRecord, "name" | "setId" | "collectorNumber" | "riftboundId">,
): number {
  let score = 0;
  const print = (hit.printNumber ?? "").toLowerCase();
  const exp = (hit.expansion?.code ?? "").toUpperCase();
  const set = (card.setId ?? "").toUpperCase();
  const num = card.collectorNumber;

  if (set && num != null) {
    const padded = String(num).padStart(3, "0");
    if (print === `${set}-${num}`.toLowerCase() || print === `${set}-${padded}`.toLowerCase()) score += 100;
    else if (print === String(num) || print === padded || print === String(num).padStart(3, "0")) {
      score += exp === set ? 90 : 40;
    } else if (print.includes(String(num)) && (!exp || exp === set)) score += 50;
    if (exp === set) score += 20;
  }
  if (card.riftboundId && print.includes(card.riftboundId.toLowerCase().split("-").slice(0, 2).join("-"))) {
    score += 30;
  }
  if (hit.name && card.name && hit.name.toLowerCase().includes(card.name.toLowerCase().split(" - ")[0]!)) {
    score += 10;
  }
  return score;
}

/** Resolve a RiftHub card to a CardNexus productId via catalogue search. */
export async function resolveProductId(
  auth: CnAuth,
  card: Pick<CardRecord, "id" | "name" | "setId" | "collectorNumber" | "riftboundId">,
  cache?: Map<string, number>,
): Promise<number | null> {
  if (cache?.has(card.id)) return cache.get(card.id)!;

  // Free-text `name` understands expansion+number (ogn-12, ogn 12, ogn12).
  // Exact `printNumber` is a separate exact match — try bare numbers and set forms.
  const attempts: Array<Record<string, unknown>> = [];

  if (card.setId && card.collectorNumber != null) {
    const set = card.setId;
    const num = card.collectorNumber;
    const padded = String(num).padStart(3, "0");
    attempts.push({ name: `${set}-${num}`, limit: 5 });
    attempts.push({ name: `${set} ${num}`, limit: 5 });
    attempts.push({ name: `${set}${padded}`, limit: 5 });
    attempts.push({ printNumber: `${set}-${num}`, limit: 5 });
    attempts.push({ printNumber: `${set}-${padded}`, limit: 5 });
    attempts.push({ printNumber: padded, limit: 10 });
    attempts.push({ printNumber: String(num), limit: 10 });
  }
  if (card.riftboundId) {
    attempts.push({ name: card.riftboundId, limit: 5 });
    const parts = card.riftboundId.split("-");
    if (parts.length >= 2) attempts.push({ name: `${parts[0]}-${parts[1]}`, limit: 5 });
  }
  attempts.push({ name: card.name, limit: 8 });

  let best: { id: number; score: number } | null = null;

  for (const body of attempts) {
    const res = await searchProducts(auth, body);
    for (const hit of res.data ?? []) {
      if (hit?.id == null) continue;
      const score = scoreProductHit(hit, card);
      if (!best || score > best.score) best = { id: hit.id, score };
      // Strong pin — stop early
      if (score >= 90) {
        cache?.set(card.id, hit.id);
        return hit.id;
      }
    }
    if (best && best.score >= 60) break;
  }

  if (best && best.score >= 30) {
    cache?.set(card.id, best.id);
    return best.id;
  }
  return null;
}

export type LocalProductMap = Record<string, number>;

export function readLocalProductMap(): LocalProductMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(CN_PRODUCT_MAP_STORAGE);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LocalProductMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writeLocalProductMap(map: LocalProductMap): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(CN_PRODUCT_MAP_STORAGE, JSON.stringify(map));
}

export function mergeLocalProductMap(entries: Array<{ cardId: string; productId: number }>): LocalProductMap {
  const map = readLocalProductMap();
  for (const e of entries) {
    if (e.cardId && e.productId) map[e.cardId] = e.productId;
  }
  writeLocalProductMap(map);
  return map;
}
